import { apiFetch } from './api';

export type InterviewLanguage = 'nl' | 'en' | 'ar';

export const INTERVIEW_LANGUAGE_OPTIONS: Array<{ value: InterviewLanguage; label: string }> = [
  { value: 'nl', label: 'Nederlands' },
  { value: 'en', label: 'Engels' },
  { value: 'ar', label: 'Arabisch' },
];

interface UploadAudioOptions {
  blob: Blob;
  conversationId: string;
  recordingType: 'conversation' | 'voice-over';
  interviewLanguage?: InterviewLanguage;
}

const UPLOAD_EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
};

// A joined take is re-encoded as WAV and a single session stays WebM, so a
// hardcoded .mp3 misdescribed almost every upload. Anything sniffing the
// extension would be reading the wrong format.
const uploadFileName = (blob: Blob): string => {
  const mimeType = (blob.type || '').split(';')[0].trim().toLowerCase();
  return `recording.${UPLOAD_EXTENSIONS[mimeType] ?? 'webm'}`;
};

/**
 * Upload an audio recording chunk, explicitly start transcription, and return the workflow_id.
 */
export async function uploadAudio({
  blob,
  conversationId,
  recordingType,
  interviewLanguage,
}: UploadAudioOptions): Promise<string> {
  const formData = new FormData();
  formData.append('file', blob, uploadFileName(blob));
  formData.append('conversation_id', conversationId);
  formData.append('recording_type', recordingType);
  formData.append('is_final_chunk', 'true');

  const uploadResponse = await apiFetch('/audio/upload_chunk', {
    method: 'POST',
    body: formData,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`);
  }

  await uploadResponse.json();

  const languageQuery = recordingType === 'conversation' && interviewLanguage
    ? `?interview_language=${encodeURIComponent(interviewLanguage)}`
    : '';
  const transcribeResponse = await apiFetch(
    `/conversation/${conversationId}/recordings/${recordingType}/transcribe${languageQuery}`,
    { method: 'POST' }
  );

  if (!transcribeResponse.ok) {
    throw new Error(`Transcription failed: ${transcribeResponse.status}`);
  }

  const data = await transcribeResponse.json();
  if (!data.workflow_id) {
    throw new Error('No workflow_id returned from transcription');
  }

  return data.workflow_id;
}
